import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const PAKKET_ID_PROPERTY = '_pakket_id';
const PAKKET_ROLE_PROPERTY = '_pakket_rol';
const CLEANUP_SOURCE = 'box-builder-cleanup';

/**
 * Removes verpakking line items left behind once every component of their
 * package has been removed from the cart elsewhere (cart page, cart drawer).
 *
 * @param {{items: Array<{key: string, properties?: Record<string, string>}>}} cart
 */
async function removeOrphanWrapLines(cart) {
  if (!cart?.items) return;

  /** @type {Map<string, {hasComponent: boolean, wraps: Array<{key: string}>}>} */
  const groups = new Map();

  for (const item of cart.items) {
    const pakketId = item.properties?.[PAKKET_ID_PROPERTY];
    if (!pakketId) continue;

    const group = groups.get(pakketId) || { hasComponent: false, wraps: [] };

    if (item.properties?.[PAKKET_ROLE_PROPERTY] === 'verpakking') {
      group.wraps.push(item);
    } else {
      group.hasComponent = true;
    }

    groups.set(pakketId, group);
  }

  const linesToRemove = [];
  for (const group of groups.values()) {
    if (!group.hasComponent) linesToRemove.push(...group.wraps);
  }

  if (linesToRemove.length === 0) return;

  for (const line of linesToRemove) {
    await fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', { body: JSON.stringify({ id: line.key, quantity: 0 }) })
    );
  }

  const response = await fetch('/cart.js');
  const updatedCart = await response.json();

  const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
  deferredUpdatePromise.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
    detail: { source: CLEANUP_SOURCE },
  });

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'remove',
      context: 'cart',
      lines: linesToRemove.map((line) => ({ id: line.key, quantity: 0 })),
      promise: deferredUpdatePromise.promise,
      source: CLEANUP_SOURCE,
    })
  );
}

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  if (event.source === CLEANUP_SOURCE) return;

  const response = await fetch('/cart.js');
  const cart = await response.json();
  removeOrphanWrapLines(cart);
});
